import { get } from "idb-keyval";

import { postThread } from "services/dbApi/methods/postThread";
import { putThreadMessages } from "services/dbApi/methods/putThreadMessages";
import type {
  ImportProgress,
  ImportResult,
} from "services/dbApi/utilities/exportImport";
import type { ChatThread } from "types/ChatThread";
import type { Message } from "types/Message";

/**
 * Errors thrown by `utilities/request` carry the fetch `Response` so
 * callers can branch on status. Narrows an untyped `unknown` from `catch`.
 */
function isResponseError(error: unknown): error is { response: Response } {
  return (
    typeof error === "object" &&
    error !== null &&
    "response" in error &&
    error.response instanceof Response
  );
}

/**
 * Walks legacy IndexedDB and uploads each thread to the API without ever
 * materializing an aggregate JSON string. Mirrors the per-thread loop in
 * `importThreadsFromJson` but bypasses the JSON file roundtrip that hits
 * V8's max-string-length on huge stores.
 *
 * `onProgress` is called once before the loop with the parsed `total`, then
 * before each thread (with `currentThreadName` set), and again after each
 * thread (with `currentThreadName` reset to `""`).
 */
export async function migrateFromLegacyIdb(
  getAccessToken: () => Promise<string>,
  onProgress?: (state: ImportProgress) => void
): Promise<ImportResult> {
  const chatThreads =
    (await get<Record<string, ChatThread>>("chatThreads")) ?? {};

  const ids = Object.keys(chatThreads);

  const progress: ImportProgress = {
    total: ids.length,
    imported: 0,
    skipped: 0,
    failed: 0,
    currentThreadName: "",
  };

  const emit = () => onProgress?.({ ...progress });
  emit();

  for (const id of ids) {
    const thread = chatThreads[id];
    progress.currentThreadName = thread.name;
    emit();

    const messages = (await get<Message[]>(`messages-${id}`)) ?? [];

    let createdThread: ChatThread;
    try {
      createdThread = await postThread(thread, getAccessToken);
    } catch (error: unknown) {
      if (isResponseError(error) && error.response.status === 409) {
        progress.skipped += 1;
      } else {
        console.error(`Failed to import thread ${id}:`, error);
        progress.failed += 1;
      }
      progress.currentThreadName = "";
      emit();
      continue;
    }

    try {
      await putThreadMessages(createdThread.id, messages, getAccessToken);
      progress.imported += 1;
    } catch (error: unknown) {
      console.error(
        `Created thread ${createdThread.id} but failed to PUT messages:`,
        error
      );
      progress.failed += 1;
    }
    progress.currentThreadName = "";
    emit();
  }

  return {
    imported: progress.imported,
    skipped: progress.skipped,
    failed: progress.failed,
  };
}
